import {
  restResourceResolver,
  batchRestResourceResolver,
} from './restResourceResolver/restResourceResolver';
import {
  announcementsResolver,
  dismissAnnouncement,
} from './resolvers/announcements';
import {
  uploadCardCover,
  updateCardDueComplete,
  updateCardCover,
  updateCardList,
  createCardTemplate,
  createCard,
  copyCard,
  deleteCard,
  changeCardDueDate,
  updateCardDates,
  updateCardRole,
  updateCardName,
  possibleCardRoleResolver,
} from './resolvers/card';
import {
  addChecklist,
  deleteChecklist,
  updateChecklistName,
  updateChecklistPos,
} from './resolvers/checklist';
import {
  addCheckItem,
  deleteCheckItem,
  updateCheckItemDueDate,
  updateCheckItemName,
  updateCheckItemPos,
  updateCheckItemState,
} from './resolvers/checkItem';
import { emailProviderResolver } from './resolvers/emailProvider';
import {
  addOneTimeMessagesDismissed,
  updateUserLocale,
  updateTeamifyVoluntaryDone,
  deleteOneTimeMessagesDismissed,
  prepMemberForAtlassianAccountOnboarding,
  prepMemberForEmailHygiene,
  memberAgreementsResolver,
  memberAtlassianAccountsResolver,
  memberAtlassianOrganizationsResolver,
  memberCardsResolver,
  acceptDeveloperTerms,
  updateMemberAvatarSource,
  uploadMemberAvatar,
  addCampaign,
  updateCampaign,
  resendVerificationEmail,
  changeMemberEmail,
  updateMemberActiveChannel,
} from './resolvers/member';
import {
  addFreeTrial,
  applyBCDiscount,
  organizationOwnedPluginsResolver,
  organizationStatsResolver,
  organizationCardStatsResolver,
  organizationLabelNamesResolver,
  organizationNewBillableGuestsResolver,
  createOrganization,
  copyBoardToOrg,
  deleteOrganizationLogo,
  addMembersToOrg,
  removeMembersFromWorkspace,
  updateOrganization,
  uploadOrganizationImage,
  organizationCardsResolver,
  organizationMemberCardsResolver,
} from './resolvers/organization';
import {
  upgradePriceQuotesResolver,
  newSubscriptionListPriceQuotesResolver,
  newSubscriptionPriceQuotesResolver,
  renewalPriceQuotesResolver,
  statementsResolver,
  createWorkspacePaidAccount,
  addMembersPriceQuotesResolver,
  createGoldPaidAccount,
  certCaptureTokenResolver,
  activateGoldCredit,
  reactivateWorkspacePaidAccount,
  reactivateGoldPaidAccount,
  updateCreditCardFactory,
  updatePaidProductFactory,
  updateBillingContactDetailsFactory,
  updateInvoiceDetailsFactory,
  cancelPaidAccountFactory,
} from './resolvers/paidAccount';
import {
  publicPluginsResolver,
  pluginCategoriesResolver,
} from './resolvers/publicPlugin';
import { unsplashPhotosResolver } from './resolvers/unsplash';
import { addButlerButton } from './resolvers/butlerButton';
import {
  enablePlugin,
  updateBoardOrg,
  updateBoardVisibility,
  updateBoardCardCoversPref,
  boardExportResolver,
  startBoardExport,
  statsResolver,
  boardDashboardViewTileResolver,
} from './resolvers/board';
import {
  createPlugin,
  deletePlugin,
  updatePlugin,
  createPluginListing,
  updatePluginListing,
  deletePluginListing,
  pluginCollaboratorsResolver,
  addPluginCollaborator,
  removePluginCollaborator,
} from './resolvers/plugin';
import {
  templateCategoriesResolver,
  templateLanguagesResolver,
  templateGalleryResolver,
} from './resolvers/template';
import {
  enterpriseClaimableOrganizationsResolver,
  transferrableDataForOrganizationResolver,
  claimOrganization,
  linkEnterpriseWithAtlassianOrganization,
  managedMembersWithTokensResolver,
  deleteManagedMemberTokens,
  deleteAllManagedMemberTokens,
  updateEnterpriseApiTokenCreationPermission,
} from './resolvers/enterprise';
import { modelTypeResolver } from './resolvers/modelTypeResolver';
import {
  createDashboardViewTile,
  updateDashboardViewTile,
  deleteDashboardViewTile,
} from './resolvers/boardDashboard';
import {
  boardHistoryResolver,
  getCardsPerList,
  getCardsPerLabel,
  getCardsPerMember,
  getCardsPerDueDateStatus,
} from './resolvers/boardHistory';
import { qrCodeResolver } from './resolvers/qrCode';
import { Resolvers } from '@apollo/client';

export const queryMap = {
  announcements: announcementsResolver,
  board: restResourceResolver,
  boards: batchRestResourceResolver,
  card: restResourceResolver,
  cards: batchRestResourceResolver,
  certCaptureToken: certCaptureTokenResolver,
  checklist: restResourceResolver,
  checklists: batchRestResourceResolver,
  list: restResourceResolver,
  lists: batchRestResourceResolver,
  atlassianAccounts: memberAtlassianAccountsResolver,
  emailProvider: emailProviderResolver,
  member: restResourceResolver,
  modelType: modelTypeResolver,
  members: batchRestResourceResolver,
  memberCards: memberCardsResolver,
  organization: restResourceResolver,
  organizations: batchRestResourceResolver,
  organizationMemberCards: organizationMemberCardsResolver,
  organizationView: restResourceResolver,
  organizationViews: batchRestResourceResolver,
  label: restResourceResolver,
  labels: batchRestResourceResolver,
  enterprise: restResourceResolver,
  enterprises: batchRestResourceResolver,
  publicPlugins: publicPluginsResolver,
  newSubscriptionListPriceQuotes: newSubscriptionListPriceQuotesResolver,
  newSubscriptionPriceQuotes: newSubscriptionPriceQuotesResolver,
  upgradePriceQuotes: upgradePriceQuotesResolver,
  renewalPriceQuotes: renewalPriceQuotesResolver,
  statements: statementsResolver,
  addMembersPriceQuotes: addMembersPriceQuotesResolver,
  search: restResourceResolver,
  plugin: restResourceResolver,
  plugins: batchRestResourceResolver,
  pluginCategories: pluginCategoriesResolver,
  qrCode: qrCodeResolver,
  unsplashPhotos: unsplashPhotosResolver,
  templateCategories: templateCategoriesResolver,
  templateLanguages: templateLanguagesResolver,
  templateGallery: templateGalleryResolver,
};

export const resolvers = {
  Query: queryMap,
  Mutation: {
    addButlerButton,
    addChecklist,
    addOneTimeMessagesDismissed,
    createOrganization,
    copyBoardToOrg,
    updateBoardOrg,
    updateBoardVisibility,
    updateBoardCardCoversPref,
    dismissAnnouncement,
    deleteChecklist,
    updateChecklistName,
    updateChecklistPos,
    addCheckItem,
    deleteCheckItem,
    addCampaign,
    updateCampaign,
    updateCheckItemDueDate,
    updateCheckItemName,
    updateCheckItemPos,
    updateCheckItemState,
    updateUserLocale,
    updateTeamifyVoluntaryDone,
    updateOrganization,
    prepMemberForAtlassianAccountOnboarding,
    prepMemberForEmailHygiene,
    deleteOneTimeMessagesDismissed,
    createPlugin,
    deletePlugin,
    enablePlugin,
    updatePlugin,
    createPluginListing,
    updatePluginListing,
    deletePluginListing,
    addPluginCollaborator,
    removePluginCollaborator,
    acceptDeveloperTerms,
    updateMemberAvatarSource,
    updateCardCover,
    uploadCardCover,
    updateCardDueComplete,
    updateCardList,
    updateCardName,
    uploadMemberAvatar,
    changeMemberEmail,
    updateMemberActiveChannel,
    resendVerificationEmail,
    addMembersToOrg,
    removeMembersFromWorkspace,
    createCardTemplate,
    createCard,
    copyCard,
    deleteCard,
    changeCardDueDate,
    updateCardDates,
    startBoardExport,
    claimOrganization,
    linkEnterpriseWithAtlassianOrganization,
    createWorkspacePaidAccount,
    createGoldPaidAccount,
    activateGoldCredit,
    reactivateWorkspacePaidAccount,
    reactivateGoldPaidAccount,
    updateBusinessClassCreditCard: updateCreditCardFactory('organizations'),
    updateGoldCreditCard: updateCreditCardFactory('members'),
    updateBusinessClassPaidProduct: updatePaidProductFactory('organizations'),
    updateGoldPaidProduct: updatePaidProductFactory('members'),
    updateBusinessClassBillingContactDetails: updateBillingContactDetailsFactory(
      'organizations',
    ),
    updateGoldBillingContactDetails: updateBillingContactDetailsFactory(
      'members',
    ),
    cancelWorkspacePaidAccount: cancelPaidAccountFactory('organizations'),
    cancelGoldPaidAccount: cancelPaidAccountFactory('members'),
    updateBusinessClassBillingInvoiceDetails: updateInvoiceDetailsFactory(
      'organizations',
    ),
    updateGoldBillingInvoiceDetails: updateInvoiceDetailsFactory('members'),
    uploadOrganizationImage,
    deleteOrganizationLogo,
    addFreeTrial,
    applyBCDiscount,
    deleteManagedMemberTokens,
    deleteAllManagedMemberTokens,
    updateEnterpriseApiTokenCreationPermission,
    createDashboardViewTile,
    updateDashboardViewTile,
    deleteDashboardViewTile,
    updateCardRole,
  },

  // Custom type resolvers

  Board: {
    export: boardExportResolver,
    exports: boardExportResolver,
    stats: statsResolver,
    dashboardViewTile: boardDashboardViewTileResolver,
    history: boardHistoryResolver,
  },

  Board_History: {
    cardsPerList: getCardsPerList,
    cardsPerLabel: getCardsPerLabel,
    cardsPerMember: getCardsPerMember,
    cardsPerDueDateStatus: getCardsPerDueDateStatus,
  },

  Card: {
    possibleCardRole: possibleCardRoleResolver,
  },

  Enterprise: {
    claimableOrganizations: enterpriseClaimableOrganizationsResolver,
    transferrableData: transferrableDataForOrganizationResolver,
    managedMembersWithTokens: managedMembersWithTokensResolver,
  },

  Member: {
    agreements: memberAgreementsResolver,
    atlassianOrganizations: memberAtlassianOrganizationsResolver,
  },

  Organization: {
    ownedPlugins: organizationOwnedPluginsResolver,
    stats: organizationStatsResolver,
    newBillableGuests: organizationNewBillableGuestsResolver,
    cards: organizationCardsResolver,
  },

  Organization_Stats: {
    cards: organizationCardStatsResolver,
    labelNames: organizationLabelNamesResolver,
  },

  Plugin: {
    collaborators: pluginCollaboratorsResolver,
  },
} as Resolvers;
